import type { Evt } from '../envelope';
import type { Capabilities, DeviceProfile, HapticKind, PlatformBridge, PlatformId } from '../PlatformBridge';
import { WebBridge } from './web';

/**
 * Capacitor 어댑터.
 *
 * @capacitor/* 플러그인을 정적 import 하지 않는다. 웹 번들이 네이티브 전용 코드를
 * 끌고 다니면 안 되고, 플러그인이 설치되지 않은 상태에서도 앱이 부팅되어야 한다.
 * 따라서 전부 동적 import 하고, 실패하면 그 기능만 웹 폴백으로 내린다.
 */
/**
 * 플러그인의 "우리가 쓰는 만큼"만 타입으로 적는다.
 * @capacitor/* 를 정적 import 하지 않으므로 실제 타입을 끌어올 수 없고,
 * 끌어와서도 안 된다 — 웹 번들이 네이티브 전용 코드를 물면 안 된다.
 */
interface KeepAwakePlugin { keepAwake(): Promise<void>; allowSleep(): Promise<void> }
interface HapticsPlugin {
  impact(o: { style: string }): Promise<void>;
  notification(o: { type: string }): Promise<void>;
}
interface StatusBarPlugin { hide(): Promise<void>; show(): Promise<void> }
interface SharePlugin { share(o: { title?: string; url?: string }): Promise<unknown> }
interface FilesystemPlugin {
  writeFile(o: { path: string; data: string; directory: string; recursive: boolean }): Promise<{ uri: string }>;
}
interface DevicePlugin { getInfo(): Promise<Record<string, unknown>> }
interface AppPlugin {
  addListener(e: string, cb: (s: { isActive: boolean }) => void): Promise<{ remove(): void }>;
}

export class CapacitorBridge implements PlatformBridge {
  readonly platform: PlatformId;
  capabilities: Capabilities;

  #fallback = new WebBridge();
  #listeners = new Set<(e: Evt) => void>();
  #removeAppListener: (() => void) | null = null;

  // 동적으로 확보한 플러그인 핸들
  #keepAwakePlugin: KeepAwakePlugin | null = null;
  #haptics: HapticsPlugin | null = null;
  #statusBar: StatusBarPlugin | null = null;
  #share: SharePlugin | null = null;
  #filesystem: FilesystemPlugin | null = null;
  #device: DevicePlugin | null = null;

  constructor(platform: PlatformId) {
    this.platform = platform;
    this.capabilities = { ...this.#fallback.capabilities, reminders: false };
  }

  async init(): Promise<void> {
    await this.#fallback.init();

    this.#keepAwakePlugin = await load<KeepAwakePlugin>('@capacitor-community/keep-awake', 'KeepAwake');
    this.#haptics = await load<HapticsPlugin>('@capacitor/haptics', 'Haptics');
    this.#statusBar = await load<StatusBarPlugin>('@capacitor/status-bar', 'StatusBar');
    this.#share = await load<SharePlugin>('@capacitor/share', 'Share');
    this.#filesystem = await load<FilesystemPlugin>('@capacitor/filesystem', 'Filesystem');
    this.#device = await load<DevicePlugin>('@capacitor/device', 'Device');

    this.capabilities = {
      keepAwake: this.#keepAwakePlugin !== null || this.#fallback.capabilities.keepAwake,
      immersive: this.#statusBar !== null || this.#fallback.capabilities.immersive,
      haptics: this.#haptics !== null || this.#fallback.capabilities.haptics,
      fileExport: this.#filesystem !== null || true,
      reminders: false,
      deviceProfile: true,
    };

    // 앱이 백그라운드로 가는 것은 세션 무효화 사유다. 반드시 잡아서 올린다.
    const app = await load<AppPlugin>('@capacitor/app', 'App');
    if (app) {
      const handle = await app.addListener('appStateChange', ({ isActive }) => {
        this.#emit({ v: 1, kind: 'evt', topic: 'appState', payload: { isActive } });
      });
      this.#removeAppListener = () => handle.remove();
    }
  }

  async keepAwake(on: boolean): Promise<void> {
    if (this.#keepAwakePlugin) {
      await (on ? this.#keepAwakePlugin.keepAwake() : this.#keepAwakePlugin.allowSleep()).catch(() => undefined);
      return;
    }
    await this.#fallback.keepAwake(on);
  }

  async setImmersive(on: boolean): Promise<void> {
    if (this.#statusBar) {
      await (on ? this.#statusBar.hide() : this.#statusBar.show()).catch(() => undefined);
      return;
    }
    await this.#fallback.setImmersive(on);
  }

  async haptic(kind: HapticKind): Promise<void> {
    if (this.#haptics) {
      try {
        if (kind === 'light') await this.#haptics.impact({ style: 'LIGHT' });
        else await this.#haptics.notification({ type: kind === 'success' ? 'SUCCESS' : 'ERROR' });
        return;
      } catch { /* 폴백으로 */ }
    }
    await this.#fallback.haptic(kind);
  }

  async exportFile(name: string, mime: string, data: Blob): Promise<void> {
    if (this.#filesystem && this.#share) {
      try {
        const b64 = await blobToBase64(data);
        const { uri } = await this.#filesystem.writeFile({
          path: name, data: b64, directory: 'CACHE', recursive: true,
        });
        await this.#share.share({ title: name, url: uri });
        return;
      } catch { /* 폴백으로 */ }
    }
    await this.#fallback.exportFile(name, mime, data);
  }

  async getDeviceProfile(): Promise<DeviceProfile> {
    const web = await this.#fallback.getDeviceProfile();
    const base: DeviceProfile = { ...web, platform: this.platform };
    if (!this.#device) return base;
    try {
      const info = await this.#device.getInfo();
      return {
        ...base,
        model: typeof info.model === 'string' ? info.model : null,
        osVersion: typeof info.osVersion === 'string' ? info.osVersion : null,
      };
    } catch {
      return base;
    }
  }

  onNativeEvent(cb: (e: Evt) => void): () => void {
    this.#listeners.add(cb);
    const offWeb = this.#fallback.onNativeEvent(cb);
    return () => { this.#listeners.delete(cb); offWeb(); };
  }

  #emit(e: Evt): void { for (const l of this.#listeners) l(e); }

  dispose(): void {
    this.#removeAppListener?.();
    this.#listeners.clear();
    this.#fallback.dispose();
  }
}

/** 설치되지 않은 플러그인은 조용히 null 을 준다. */
async function load<T>(mod: string, exportName: string): Promise<T | null> {
  try {
    const m = (await import(/* @vite-ignore */ mod)) as Record<string, unknown>;
    return (m[exportName] as T) ?? null;
  } catch {
    return null;
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('blob read failed'));
    r.onload = () => { const s = String(r.result); resolve(s.slice(s.indexOf(',') + 1)); };
    r.readAsDataURL(blob);
  });
}
